<template>
  <div>
    <div
      v-if="editor"
      class="editor-wrapper q-mt-md q-pl-sm row no-wrap"
    >
      <div class="flex justify-center items-center q-pr-sm">
        <slot name="prepend" />
      </div>
      <editor-content
        class="input-with-mentions full-width"
        :editor="editor"
      />
    </div>
  </div>
</template>

<script>
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import { Editor, EditorContent, VueRenderer } from '@tiptap/vue-3';
import { callFirebaseFunction } from 'boot/firebase';
import MentionList from 'components/UI/MentionList';
import { head } from 'lodash';
import { mapState } from 'pinia';
import NormalizePastedText from 'src/editor-extensions/normalizePastedText';
import OverrideEnterKey from 'src/editor-extensions/overrideEnterKey';
import UserMention from 'src/editor-extensions/userMention';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { getClientUsers } from 'src/utils/OfflineAccessUtils';
import tippy from 'tippy.js';

export default {
  name: 'TiptapInput',

  components: {
    EditorContent,
  },

  props: {
    modelValue: {
      type: Object,
      default: () => ({
        text: '',
        html: '',
      }),
    },

    sessionId: {
      type: String,
      default: null,
    },

    clientId: {
      type: String,
      default: null,
    },

    visibilitySettings: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['submit', 'update:modelValue', 'focus', 'blur'],

  data() {
    return {
      editor: null,
      users: [],
      loadingUsers: false,
      shouldUpdateMentionedUsers: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient']),
    ...mapState(network, ['offlineMode']),
  },

  watch: {
    modelValue(value) {
      const isSame = this.editor.getHTML() === value.html;

      if (isSame) {
        return;
      }

      this.editor.commands.setContent(value.html, false);
    },
    visibilitySettings() {
      if (this.shouldUpdateMentionedUsers) {
        return;
      }

      this.shouldUpdateMentionedUsers = true;
    },
  },

  mounted() {
    this.editor = new Editor({
      autofocus: true,
      content: this.modelValue.html,
      extensions: [
        OverrideEnterKey.configure({
          onEnterPress: () => {
            this.$emit('submit');
          },
        }),
        // disable all default plugins
        StarterKit.configure({
          blockquote: false,
          bold: false,
          bulletList: false,
          code: false,
          codeBlock: false,
          dropcursor: false,
          gapcursor: false,
          hardBreak: false,
          heading: false,
          history: false,
          horizontalRule: false,
          italic: false,
          listItem: false,
          orderedList: false,
          strike: false,
        }),
        Placeholder.configure({
          placeholder: this.$t('sessions.sessionCard.inputAddComment'),
        }),
        UserMention.configure({
          HTMLAttributes: {
            class: 'user-mention',
          },
          suggestion: {
            items: ({ query }) => {
              return this.users.filter((item) => {
                return item.userName.toLowerCase().startsWith(query.toLowerCase());
              });
            },

            render: () => {
              let component;
              let popup;

              return {
                onStart: (props) => {
                  if (!this.users.length || this.shouldUpdateMentionedUsers) {
                    this.getMentionUsers().then(() => {
                      const tippyPopup = head(popup);
                      component.updateProps({
                        items: this.users,
                        loading: this.loadingUsers,
                      });
                      tippyPopup.hide();
                      tippyPopup.show();
                    });
                  }

                  component = new VueRenderer(MentionList, {
                    props: {
                      ...props,
                      loading: this.loadingUsers,
                    },
                    editor: props.editor,
                  });

                  popup = tippy('body', {
                    getReferenceClientRect: props.clientRect,
                    appendTo: () => document.body,
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                  });
                },

                onUpdate(props) {
                  component.updateProps(props);

                  if (!props.clientRect) {
                    return;
                  }

                  popup[0].setProps({
                    getReferenceClientRect: props.clientRect,
                  });
                },

                onKeyDown(props) {
                  if (props.event.key === 'Escape') {
                    popup[0].hide();

                    return true;
                  }

                  return component.ref?.onKeyDown(props);
                },

                onExit() {
                  popup[0].destroy();
                  component.destroy();
                },
              };
            },
          },
        }),
        NormalizePastedText,
      ],
      onUpdate: () => {
        this.$emit('update:modelValue', {
          text: this.editor.getText(),
          html: this.editor.getHTML(),
        });
      },
      onFocus: () => {
        this.$emit('focus');
      },
      onBlur: () => {
        this.$emit('blur');
      },
    });
  },

  beforeUnmount() {
    this.editor.destroy();
  }, // beforeUnmount()

  methods: {
    async getMentionUsers() {
      this.loadingUsers = true;

      let clientData;

      if (!this.offlineMode) {
        clientData = await callFirebaseFunction('getClientUsers', {
          clientId: this.clientId || this.currentClient.id,
          clinicId: this.userDetails.clinic,
          sessionId: this.sessionId,
        });
      } else {
        clientData = await getClientUsers({
          clientId: this.clientId || this.currentClient.id,
          clinicId: this.userDetails.clinic,
          sessionId: this.sessionId,
          userId: this.userDetails.userId,
        });
      }

      this.users = clientData.data;
      this.shouldUpdateMentionedUsers = false;
      this.loadingUsers = false;
    }, // getMentionUsers()
  },
};
</script>

<style lang="scss">
/* Basic editor styles */
.input-with-mentions .ProseMirror {
  padding: 18px 12px 12px 0;
  min-height: 56px;

  &:focus-visible {
    outline: none;
    // @todo: border on focus
    // border: 1.5px solid $primary;
  }

  > * + * {
    margin-top: 0.75em;
  }

  p {
    margin: 0;

    &.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: rgba(0, 0, 0, 0.6);
      pointer-events: none;
      font-size: 16px;
      height: 0;
    }
  }
}

.editor-wrapper {
  border: 1px solid var(--app-outline, rgba(0,0,0,0.24));
  border-radius: 4px;
}

.tooltip-shortcut {
  font-size: 0.6rem;
}
</style>
